import type {
  ReconciliationRunRequest,
  ShareAdjustmentProposal,
} from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { OPERATIONS_ENGINE_ERROR_KEYS } from '../vaults/helpers';
import {
  DELTA_PATTERN,
  HOLDER_ID_PATTERN,
  REASON_CODE_PATTERN,
  RFC3339_PATTERN,
  RECONCILIATION_NS as NS,
  TEXT_MAX,
} from './constants';

type Translate = (key: string, options?: object) => string;

/** One idempotency key per click: 1–64 letters, digits, hyphens or underscores, kept while the click is retried. */
export const newRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const sameOperator = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

const codePoints = (value: string) => Array.from(value.trim()).length;

const err = (key: string) => `${NS}.errors.${key}`;

/** A required 1–TEXT_MAX text, counted as the BFF counts it (trimmed, code points). */
const textError = (value: string): string | undefined => {
  const n = codePoints(value);
  if (n === 0) return err('required');
  return n > TEXT_MAX ? err('tooLong') : undefined;
};

export interface ProposalForm {
  /** True: a share-neutral adjustment (no holder, delta "0") — how a break that moved no shares is resolved. */
  shareNeutral: boolean;
  holderId: string;
  deltaShares: string;
  reasonCode: string;
  memo: string;
  evidenceRef: string;
  /** The break this proposal resolves: both parts or neither (a pure correction). */
  breakValuedAt: string;
  breakAttemptSeq: string;
}

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** The i18n key of each invalid field of a proposal; `{}` when the form can be sent. */
export const validateProposal = (form: ProposalForm): FieldErrors<keyof ProposalForm> => {
  const out: FieldErrors<keyof ProposalForm> = {};
  if (!form.shareNeutral) {
    const holder = form.holderId.trim();
    if (!HOLDER_ID_PATTERN.test(holder)) out.holderId = err('holderFormat');
    const delta = form.deltaShares.trim();
    if (!DELTA_PATTERN.test(delta)) out.deltaShares = err('deltaFormat');
    else if (Number(delta) === 0) out.deltaShares = err('deltaZero');
  }
  if (!REASON_CODE_PATTERN.test(form.reasonCode.trim())) out.reasonCode = err('reasonCodeFormat');
  const memo = textError(form.memo);
  if (memo) out.memo = memo;
  const evidence = textError(form.evidenceRef);
  if (evidence) out.evidenceRef = evidence;
  const at = form.breakValuedAt.trim();
  const seq = form.breakAttemptSeq.trim();
  if (at && !RFC3339_PATTERN.test(at)) out.breakValuedAt = err('breakValuedAtFormat');
  if (seq && !/^\d+$/.test(seq)) out.breakAttemptSeq = err('breakAttemptSeqFormat');
  if (Boolean(at) !== Boolean(seq))
    out[at ? 'breakAttemptSeq' : 'breakValuedAt'] = err('breakKeyPair');
  return out;
};

export interface ResolutionForm {
  cause: string;
  evidenceRef: string;
}

export const validateResolution = (form: ResolutionForm): FieldErrors<keyof ResolutionForm> => {
  const out: FieldErrors<keyof ResolutionForm> = {};
  const cause = textError(form.cause);
  if (cause) out.cause = cause;
  const evidence = textError(form.evidenceRef);
  if (evidence) out.evidenceRef = evidence;
  return out;
};

/**
 * What the detail offers on a proposal. Approve (and resolve) only while AWAITING_APPROVAL and only to an operator who is
 * not the proposer — never guessed when the signed-in operator is unknown; the engine refuses self-approval too
 * (VAULT_ADJUSTMENT_SAME_APPROVER) and its message is shown if a stale page gets that far. Cancel while AWAITING_APPROVAL.
 */
export const adjustmentActions = (
  proposal: Pick<ShareAdjustmentProposal, 'status' | 'proposedBy'>,
  operatorId: string | undefined
): { approve: boolean; cancel: boolean } => {
  const awaiting = proposal.status === 'AWAITING_APPROVAL';
  return {
    approve:
      awaiting && Boolean(operatorId) && !sameOperator(proposal.proposedBy, operatorId as string),
    cancel: awaiting,
  };
};

/** A proposal that names a break is approved by resolving it; a pure correction by the plain approve. */
export const namesBreak = (
  proposal: Pick<ShareAdjustmentProposal, 'breakValuedAt' | 'breakAttemptSeq'>
): boolean => proposal.breakValuedAt != null && proposal.breakAttemptSeq != null;

const POLLING: ReadonlyArray<ReconciliationRunRequest['status']> = [
  'QUEUED',
  'RUNNING',
  'DEFERRED',
];

/** Whether the run still moves (keep polling) and the one fact that goes next to its status: the verdict or the error code. */
export const runOutcome = (
  run: Pick<ReconciliationRunRequest, 'status'> &
    Partial<Pick<ReconciliationRunRequest, 'errorCode' | 'attempt'>>
): { terminal: boolean; detail: string | null } => ({
  terminal: !POLLING.includes(run.status),
  detail:
    run.status === 'DONE'
      ? (run.attempt?.verdict ?? null)
      : run.status === 'FAILED'
        ? (run.errorCode ?? null)
        : null,
});

const ERROR_KEYS: Record<string, string> = {
  VAULT_ADJUSTMENT_SAME_APPROVER: 'sameApprover',
  VAULT_ADJUSTMENT_NOT_AWAITING: 'notAwaiting',
  VAULT_ADJUSTMENT_NOT_FOUND: 'proposalNotFound',
  VAULT_ADJUSTMENT_NEGATIVE_BALANCE: 'negativeBalance',
  VAULT_ADJUSTMENT_KEY_CONFLICT: 'keyConflict',
  VAULT_BREAK_MISMATCH: 'breakMismatch',
  VAULT_BREAK_ALREADY_RESOLVED: 'breakResolved',
  VAULT_BREAK_NOT_FOUND: 'breakNotFound',
  VAULT_PRICING_UNAVAILABLE: 'pricingUnavailable',
  VAULT_RUN_UNAVAILABLE: 'runUnavailable',
  VAULT_RUN_VAULT_NOT_COVERED: 'runNotCovered',
  VAULT_RUN_REQUEST_CONFLICT: 'runConflict',
  VAULT_RUN_REQUEST_NOT_FOUND: 'runRequestNotFound',
  STEP_UP_REQUIRED: 'stepUp',
  STEP_UP_UNAVAILABLE: 'stepUpUnavailable',
};

/** A refused action or read, in the console's words; the engine's own text is shown only for a refused input. */
export const reconciliationErrorMessages = (error: unknown, translate: Translate): string[] => {
  const info = parseApiError(error);
  if (info.code === 'VAULT_ADJUSTMENT_INVALID') {
    return [translate(err('invalid'), { message: info.messages[0] ?? '' })];
  }
  // A DTO validation 400 carries no code, only the validator's text: show it so the operator knows which field.
  if (info.status === 400 && !info.code && info.messages.length > 0) {
    return [translate(err('invalid'), { message: info.messages.join('; ') })];
  }
  const known = info.code ? ERROR_KEYS[info.code] : undefined;
  if (known) return [translate(err(known))];
  if (info.status === 403) return [translate(err('forbidden'))];
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return [translate(err(engine))];
  // A bare 504 (an ingress in front of the BFF) may still have applied too.
  return [translate(err(info.status === 504 ? 'timeout' : 'failed'))];
};
